
import type React from "react"

import { useState, useRef, useEffect } from "react"
import { Check, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@components/react/pricing/ui/button"
import { motion } from "framer-motion"

interface AppleStylePricingCardsProps {
  openQuoteModalWithPlan: (planName: string, planType?: string) => void
  openTrialModal: (planName?: string) => void
}

export function AppleStylePricingCards({ openQuoteModalWithPlan, openTrialModal }: AppleStylePricingCardsProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const carouselRef = useRef<HTMLDivElement>(null)

  // Touch handling
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  // Handle navigation
  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? 1 : 0))
  }

  const handleNext = () => {
    setActiveIndex((prev) => (prev === 1 ? 0 : 1))
  }

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null) // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      handleNext()
    } else if (isRightSwipe) {
      handlePrev()
    }

    // Reset values
    setTouchStart(null)
    setTouchEnd(null)
  }

  const mobileCards = [
    {
      name: "Control Plus",
      tagline: "Mission-critical data security",
      description: "Unlimited and powerful data control.",
      price: "$9",
      iconColor: "#26A69A",
      features: [
        "Data policy and insights",
        "Data owner engagement",
        "Data discovery and analysis scans (5)",
        "AI benchmarking",
        "User license management",
      ],
      recommended: true,
    },
    {
      name: "Resilience Essential",
      tagline: "Basic data security",
      description: "Out-of-the-box and configurable data protection.",
      price: "$7",
      iconColor: "#4CAF50",
      features: [
        "Data protection for Microsoft 365",
        "Data protection for Entra ID",
        "Data discovery and analysis scans (5)",
      ],
      recommended: false,
    },
  ]

  // Desktop view - Recommended card on the right
  if (!isMobile) {
    return (
      <div className="flex flex-col md:flex-row gap-8 mt-8 max-w-5xl mx-auto">
        {/* Card 1 - Resilience Essential */}
        <motion.div
          className="pricing-card group relative rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500 flex-1 flex flex-col border border-gray-50 "
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          whileHover={{
            y: -8,
            boxShadow: "0 20px 30px rgba(0, 0, 0, 0.1)",
            transition: { duration: 0.3, ease: "easeOut" },
          }}
        >
          <div className="px-8 pt-10 pb-8 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[#f8f9fa] to-white z-0"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-[#1d1d1f] mb-2">Resilience Essential</h3>
              <p className="text-sm text-[#86868b] mb-6">Basic data security</p>
              <p className="text-sm text-[#1d1d1f] mb-6 leading-relaxed">
                Out-of-the-box and configurable data protection.
              </p>

              <div className="flex flex-col mb-6">
                <div className="text-sm text-[#86868b] mb-1">Starting at</div>
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-[#1d1d1f]">$7</span>
                  <div className="ml-2">
                    <div className="text-[#1d1d1f]">User/Month</div>
                    <div className="text-sm text-[#86868b]">(Billed annually)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 pb-8 flex-1 flex flex-col">
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f5e9] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#4CAF50]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data protection for Microsoft 365</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f5e9] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#4CAF50]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data protection for Entra ID</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e8f5e9] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#4CAF50]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data discovery and analysis scans (5)</span>
              </li>
              {/* Invisible placeholder items to match height */}
              <li className="opacity-0 pointer-events-none">
                <div className="flex-shrink-0 w-5 h-5"></div>
                <span className="ml-3 text-sm">Placeholder</span>
              </li>
              <li className="opacity-0 pointer-events-none">
                <div className="flex-shrink-0 w-5 h-5"></div>
                <span className="ml-3 text-sm">Placeholder</span>
              </li>
            </ul>

            <div className="mt-auto space-y-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  className="w-full py-6 bg-[#3037e2] text-white hover:bg-[#2731c8] transition-all duration-300 flex items-center justify-center group"
                  onClick={() => openQuoteModalWithPlan("Resilience Essential")}
                >
                  <span>Get Custom Quote</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Card 2 - Control Plus - FEATURED */}
        <motion.div
          className="pricing-card group relative rounded-3xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-all duration-500 flex-1 flex flex-col border border-[#26A69A]/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          whileHover={{
            y: -8,
            boxShadow: "0 20px 30px rgba(0, 0, 0, 0.1)",
            transition: { duration: 0.3, ease: "easeOut" },
          }}
        >
          <div className="absolute top-0 right-0 z-10">
            <div className="bg-[#ef483d] text-white text-xs font-medium py-1.5 px-4 rounded-bl-2xl">RECOMMENDED</div>
          </div>

          <div className="px-8 pt-10 pb-8 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[#eef1ff]/60 to-white z-0"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-[#1d1d1f] mb-2">Control Plus</h3>
              <p className="text-sm text-[#86868b] mb-6">Mission-critical data security</p>
              <p className="text-sm text-[#1d1d1f] mb-6 leading-relaxed">
                Unlimited and powerful data control.
              </p>

              <div className="flex flex-col mb-6">
                <div className="text-sm text-[#86868b] mb-1">Starting at</div>
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-[#1d1d1f]">$9</span>
                  <div className="ml-2">
                    <div className="text-[#1d1d1f]">User/Month</div>
                    <div className="text-sm text-[#86868b]">(Billed annually)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 pb-8 flex-1 flex flex-col">
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#bbdefb] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#26A69A]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data policy and insights</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#bbdefb] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#26A69A]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data owner engagement</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#bbdefb] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#26A69A]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">Data discovery and analysis scans (5)</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#bbdefb] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#26A69A]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">AI benchmarking</span>
              </li>
              <li className="flex items-start">
                <motion.div
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-[#bbdefb] flex items-center justify-center mt-0.5 feature-check"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.7, duration: 0.4, type: "spring" }}
                >
                  <Check className="h-3 w-3 text-[#26A69A]" />
                </motion.div>
                <span className="ml-3 text-sm text-[#1d1d1f]">User license management</span>
              </li>
            </ul>

            <div className="mt-auto space-y-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  className="w-full py-6 bg-[#3037e2] text-white hover:bg-[#2731c8] transition-all duration-300 flex items-center justify-center group"
                  onClick={() => openQuoteModalWithPlan("Control Plus")}
                >
                  <span>Get Custom Quote</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  // Mobile view - Apple-style carousel with touch support
  // Using mobileCards array where recommended card is first
  return (
    <div className="mt-8 relative">
      {/* Carousel container with touch events */}
      <div
        className="relative overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          ref={carouselRef}
          className="flex py-4 transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {mobileCards.map((card, index) => (
            <div key={card.name} className="min-w-full px-4 flex justify-center">
              <motion.div
                className={`pricing-card w-full max-w-sm relative rounded-3xl overflow-hidden bg-white shadow-md transition-all duration-500 flex flex-col border ${
                  card.recommended ? "border-[#26A69A]/10 ring-2 ring-[#ef483d]/10" : "border-gray-50 "
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: activeIndex === index ? 1 : 0.7,
                  y: 0,
                  scale: activeIndex === index ? 1 : 0.95,
                }}
                transition={{ duration: 0.5 }}
              >
                {card.recommended && (
                  <div className="absolute top-0 right-0 z-10">
                    <div className="bg-[#ef483d] text-white text-xs font-medium py-1.5 px-4 rounded-bl-2xl">
                      RECOMMENDED
                    </div>
                  </div>
                )}

                <div className="px-6 pt-8 pb-6 relative">
                  <div
                    className={`absolute inset-0 bg-gradient-to-b ${
                      card.recommended ? "from-[#eef1ff]/60 to-white" : "from-[#f8f9fa] to-white"
                    } z-0`}
                  ></div>
                  <div className="relative z-10">
                    <h3 className="text-3xl font-bold text-[#1d1d1f] mb-2">{card.name}</h3>
                    <p className="text-sm text-[#86868b] mb-4">{card.tagline}</p>
                    <p className="text-sm text-[#1d1d1f] mb-6 leading-relaxed">{card.description}</p>

                    <div className="flex flex-col mb-6">
                      <div className="text-sm text-[#86868b] mb-1">Starting at</div>
                      <div className="flex items-baseline">
                        <span className="text-5xl font-bold text-[#1d1d1f]">{card.price}</span>
                        <div className="ml-2">
                          <div className="text-[#1d1d1f]">User/Month</div>
                          <div className="text-sm text-[#86868b]">(Billed annually)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6 flex-1 flex flex-col">
                  <ul className="space-y-3 mb-6 flex-1">
                    {card.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <div
                          className={`flex-shrink-0 w-5 h-5 rounded-full bg-[${card.iconColor}]/20 flex items-center justify-center mt-0.5 feature-check`}
                        >
                          <Check className={`h-3 w-3 text-[${card.iconColor}]`} />
                        </div>
                        <span className="ml-3 text-sm text-[#1d1d1f]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto space-y-4">
                    <Button
                      className="w-full py-6 bg-[#3037e2] text-white hover:bg-[#2731c8] transition-all duration-300 flex items-center justify-center group"
                      onClick={() => openQuoteModalWithPlan(card.name)}
                    >
                      <span>Get Custom Quote</span>
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Visual swipe hint - subtle arrows that fade in and out */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-10 h-10 flex items-center justify-center shadow-sm"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: [0, 0.7, 0], x: [-10, 0, -10] }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: 3,
            delay: 1,
            repeatDelay: 5,
          }}
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </motion.div>

        <motion.div
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-10 h-10 flex items-center justify-center shadow-sm"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: [0, 0.7, 0], x: [10, 0, 10] }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            duration: 3,
            repeatDelay: 5,
          }}
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </motion.div>
      </div>

      {/* Navigation arrows (still keep these for users who prefer tapping arrows) */}
      <div className="flex justify-between absolute top-1/2 left-0 right-0 -mt-6 px-2 pointer-events-none">
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md text-gray-700 pointer-events-auto"
          onClick={handlePrev}
          aria-label="Previous plan"
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-full bg-white/80 backdrop-blur-sm shadow-md text-gray-700 pointer-events-auto"
          onClick={handleNext}
          aria-label="Next plan"
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      </div>

      {/* Pagination dots */}
      <div className="flex justify-center mt-6 space-x-2">
        {mobileCards.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeIndex === index ? "bg-[#ef483d] w-6" : "bg-gray-300"
            }`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Swipe instruction - shows only on initial load */}
      <motion.div
        className="text-center text-sm text-gray-500 mt-2"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ delay: 4, duration: 1 }}
      >
        Swipe left or right to view plans
      </motion.div>
    </div>
  )
}
